import { Call, RpcProvider } from 'starknet';
import { MULTICALL_CONTRACT_ADDRESS } from '../../constants';
import { Decimal, DEFAULT_DECIMAL_PRECISION, Uint256 } from '../../datastructures';
import { getConfigManager } from '../../config';
import { MultiCallContract, ContractRevertError } from '../../services';
import { Atom, createAsyncAtom } from '../../lib/state';
import rpcProviderAtom from '../rpcProviderAtom';
import { triggerImmediateRpcFailover } from '../rpcHealthAtom';
import type { AssetConfig } from '../../interfaces';

export type TokenRateMap = {
  [tokenAddress: string]: Decimal;
};

export type AssetTokenRates = {
  rate: TokenRateMap;
  baseAssetRate: TokenRateMap;
  /** Asset tickers whose on-chain oracle reverted (e.g. broken price feed). */
  unavailableAssets: string[];
};

type AssetEntry = [string, AssetConfig];

function buildCalls(oracleAddress: string, assets: AssetEntry[]): Call[] {
  return [
    { contractAddress: oracleAddress, entrypoint: 'getBaseAssetPriceInUsd' },
    ...assets.map(
      ([, cfg]) => ({ contractAddress: cfg.oracleAddress, entrypoint: 'getAssetPrice', calldata: [cfg.address] } as Call),
    ),
  ];
}

function buildRates(
  baseAssetPriceInUsd: Uint256,
  assetPrices: { address: string; price: Uint256 }[],
  unavailableAssets: string[] = [],
): AssetTokenRates {
  const decimalBase = new Decimal(baseAssetPriceInUsd, DEFAULT_DECIMAL_PRECISION);

  const baseAssetRate: TokenRateMap = {};
  const rate: TokenRateMap = {};

  for (const { address, price } of assetPrices) {
    const decimalPrice = new Decimal(price, DEFAULT_DECIMAL_PRECISION);
    baseAssetRate[address] = decimalPrice;
    rate[address] = decimalPrice.mul(decimalBase);
  }

  return { rate, baseAssetRate, unavailableAssets };
}

/**
 * Fast path: single multicall for all assets.
 * Throws ContractRevertError if any sub-call reverts (e.g. broken oracle).
 */
const fetchViaMulicall = async (provider: RpcProvider, oracleAddress: string, assets: AssetEntry[]) => {
  const multicallContract = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);
  const result = await multicallContract.aggregateUint256ArrayAsync(buildCalls(oracleAddress, assets));

  if (!result) {
    throw new Error('Token rates multicall returned null — RPC node may be unavailable');
  }

  const [baseAssetPriceInUsd, ...tokenPrices] = result;
  return buildRates(
    baseAssetPriceInUsd,
    tokenPrices.map((price, i) => ({ address: assets[i][1].address, price })),
  );
};

/**
 * Slow fallback: call each oracle individually via provider.callContract.
 * Skips assets whose oracle reverts, so one broken asset doesn't block the rest.
 */
const fetchIndividually = async (provider: RpcProvider, oracleAddress: string, assets: AssetEntry[]) => {
  const callSingle = async (call: Call): Promise<Uint256 | null> => {
    try {
      const resp = await provider.callContract({
        contractAddress: call.contractAddress,
        entrypoint: call.entrypoint,
        calldata: call.calldata ?? [],
      });
      const [low, high] = resp;
      return { low: BigInt(low), high: BigInt(high) } as Uint256;
    } catch (err) {
      return null;
    }
  };

  const baseResult = await callSingle({
    contractAddress: oracleAddress,
    entrypoint: 'getBaseAssetPriceInUsd',
  });

  if (!baseResult) {
    throw new Error('Base asset price oracle call failed — cannot compute any token rates');
  }

  const assetResults = await Promise.all(
    assets.map(async ([ticker, cfg]) => {
      const price = await callSingle({
        contractAddress: cfg.oracleAddress,
        entrypoint: 'getAssetPrice',
        calldata: [cfg.address],
      });
      if (!price) {
        console.warn(`Oracle for ${ticker} (${cfg.address}) reverted — skipping`);
      }
      return { address: cfg.address, ticker, price };
    }),
  );

  const successfulPrices = assetResults
    .filter((r): r is { address: string; ticker: string; price: Uint256 } => r.price !== null);

  const unavailableAssets = assetResults
    .filter(r => r.price === null)
    .map(r => r.ticker);

  return buildRates(baseResult, successfulPrices, unavailableAssets);
};

const assetTokenRatesAtom: Atom<AssetTokenRates> = createAsyncAtom<AssetTokenRates>(
  async get => {
    const provider = await get(rpcProviderAtom);
    const oracleAddress = getConfigManager().getConfig().oracle;
    const assets: AssetEntry[] = Object.entries(getConfigManager().getConfig().assets)
      .filter(([, asset]) => !asset.hidden);

    try {
      const result = await fetchViaMulicall(provider, oracleAddress, assets);
      assetTokenRatesAtom.clearError();
      return result;
    } catch (error) {
      if (error instanceof ContractRevertError) {
        console.warn('Multicall reverted (broken oracle), falling back to per-asset calls');
        try {
          const result = await fetchIndividually(provider, oracleAddress, assets);
          assetTokenRatesAtom.clearError();
          return result;
        } catch (fallbackError) {
          console.error('Per-asset fallback also failed:', fallbackError);
          assetTokenRatesAtom.setError(fallbackError);
          void triggerImmediateRpcFailover('Per-asset fallback failed');
          return assetTokenRatesAtom.getResolvedValue() ?? { rate: {}, baseAssetRate: {}, unavailableAssets: [] };
        }
      }

      console.error('Failed to load token rates:', error);
      assetTokenRatesAtom.setError(error);
      void triggerImmediateRpcFailover('Failed to load token rates');
      return assetTokenRatesAtom.getResolvedValue() ?? { rate: {}, baseAssetRate: {}, unavailableAssets: [] };
    }
  },
  undefined,
  ['user-refresh'],
);
export default assetTokenRatesAtom;
